import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  LOCALE_ID,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ListItem } from '@maximizer/core/shared/domain';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  DEFAULT_RECURRENCE_INTERVAL,
  MAX_RECURRENCE_INTERVAL,
  MAX_RECURRENCE_OCCURRENCES,
  MAX_RECURRENCE_RANGE_MONTHS,
  RECURRENCE_DATE_FORMAT,
  clampRecurrenceEndsOn,
  defaultRecurrenceEndsOn,
  describeRecurrence,
  describeRecurrenceTruncation,
  exceedsRecurrenceOccurrences,
  expandRecurrenceDates,
  isRecurrenceEndsOnInRange,
  maxRecurrenceEndsOn,
  recurrenceText,
  supportsRecurrenceInterval,
} from '@maximizer/outlook/shared/data-access';
import {
  AppointmentRecurrence,
  AppointmentRepeat,
} from '@maximizer/outlook/shared/domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns';
import {
  KENDO_CHECKBOX,
  KENDO_FORMFIELD,
  KENDO_NUMERICTEXTBOX,
} from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { startOfDay } from 'date-fns';

const REPEAT_DIALOG_WIDTH = 320;
const REPEAT_OPTIONS: AppointmentRepeat[] = [
  'daily',
  'weekly',
  'monthly',
  'yearly',
];

type RepeatDialogForm = Pick<
  AppointmentRecurrence,
  'repeat' | 'interval' | 'skipWeekends' | 'startsOn'
> & { endsOn: Date | null };

function toRecurrence(
  form: RepeatDialogForm,
  seriesEndsOn?: Date | null,
): AppointmentRecurrence | null {
  if (!form.endsOn) return null;
  return {
    repeat: form.repeat,
    interval: form.interval,
    skipWeekends: form.skipWeekends,
    startsOn: startOfDay(form.startsOn),
    endsOn: startOfDay(form.endsOn),
    ...(seriesEndsOn === undefined ? {} : { seriesEndsOn }),
  };
}

function endsOnValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const form = control.parent?.value as RepeatDialogForm | undefined;
    const endsOn = control.value as Date | null;
    if (!form || !endsOn) return { required: true };
    // The interval's own validators already block Save; a half-edited interval has no occurrences to count.
    if (control.parent?.get('interval')?.invalid) return null;

    if (!isRecurrenceEndsOnInRange(form.startsOn, endsOn)) {
      return { recurrenceRange: true };
    }

    const recurrence = toRecurrence({ ...form, endsOn });
    const dates = recurrence ? expandRecurrenceDates(recurrence) : [];
    if (dates.length === 0) return { noOccurrences: true };
    if (exceedsRecurrenceOccurrences(dates))
      return { tooManyOccurrences: true };

    return null;
  };
}

@Component({
  selector: 'maximizer-appointment-repeat-dialog',
  templateUrl: './repeat-dialog.component.html',
  imports: [
    KENDO_BUTTONS,
    KENDO_CHECKBOX,
    KENDO_DATEINPUTS,
    KENDO_DIALOG,
    KENDO_DROPDOWNLIST,
    KENDO_FORMFIELD,
    KENDO_LABEL,
    KENDO_NUMERICTEXTBOX,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AppointmentRepeatDialogComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  readonly value = input<AppointmentRecurrence | null>(null);
  readonly startsOn = input.required<Date>();

  readonly saved = output<AppointmentRecurrence>();
  readonly cancelled = output<void>();

  private readonly translate = inject(TranslateService);
  private readonly localeId = inject(LOCALE_ID);
  private readonly recurrenceText = recurrenceText(
    (key, params) => this.translate.instant(key, params),
    this.localeId,
  );

  readonly dialogWidth = REPEAT_DIALOG_WIDTH;
  readonly dateFormat = RECURRENCE_DATE_FORMAT;
  readonly minInterval = DEFAULT_RECURRENCE_INTERVAL;
  readonly maxInterval = MAX_RECURRENCE_INTERVAL;
  readonly maxRangeMonths = MAX_RECURRENCE_RANGE_MONTHS;
  readonly maxOccurrences = MAX_RECURRENCE_OCCURRENCES;
  readonly repeatOptions: ListItem<AppointmentRepeat>[] = REPEAT_OPTIONS.map(
    (repeat) => ({
      id: repeat,
      name: this.translate.instant(`outlook.appointment.recurrence.${repeat}`),
    }),
  );

  private readonly draft = signal<RepeatDialogForm | null>(null);
  private readonly draftRecurrence = computed(() => {
    const draft = this.draft();
    return draft ? toRecurrence(draft, this.value()?.seriesEndsOn) : null;
  });
  readonly summary = computed(() => {
    const recurrence = this.draftRecurrence();
    return recurrence
      ? describeRecurrence(recurrence, this.recurrenceText)
      : '';
  });
  readonly truncationNote = computed(() => {
    const recurrence = this.draftRecurrence();
    return recurrence
      ? describeRecurrenceTruncation(recurrence, this.recurrenceText)
      : null;
  });
  readonly endsOnMax = computed(() =>
    maxRecurrenceEndsOn(this.draftStartsOn()),
  );
  readonly endsOnMin = computed(() => startOfDay(this.draftStartsOn()));
  // Maximizer only honours skipWeekend on daily patterns, as the core calendar UI does.
  readonly canSkipWeekends = computed(() => this.draft()?.repeat === 'daily');
  readonly canSetInterval = computed(() =>
    supportsRecurrenceInterval(this.draft()?.repeat ?? 'daily'),
  );

  form!: FormGroupWrapper<RepeatDialogForm>;

  ngOnInit(): void {
    this.form = this.buildForm();
    this.draft.set(this.form.value);
  }

  onRecurrenceFieldChange(): void {
    this.form.controls.endsOn.control.updateValueAndValidity();
  }

  onRepeatChange(repeat: AppointmentRepeat): void {
    if (repeat !== 'daily') this.form.patch({ skipWeekends: false });
    if (!supportsRecurrenceInterval(repeat)) {
      this.form.patch({ interval: DEFAULT_RECURRENCE_INTERVAL });
    }
    this.onRecurrenceFieldChange();
  }

  onStartsOnChange(): void {
    const { startsOn, endsOn } = this.form.value;
    this.form.patch({ endsOn: clampRecurrenceEndsOn(startsOn, endsOn) });
  }

  save(): void {
    const validation = this.form.validate();
    if (!validation.valid) return;

    const recurrence = toRecurrence(
      this.form.value,
      this.value()?.seriesEndsOn,
    );
    if (recurrence) this.saved.emit(recurrence);
  }

  private draftStartsOn(): Date {
    return this.draft()?.startsOn ?? this.startsOn();
  }

  private buildForm(): FormGroupWrapper<RepeatDialogForm> {
    const current = this.value();
    const startsOn = startOfDay(current?.startsOn ?? this.startsOn());

    return FormWrapperBuilder.group<RepeatDialogForm>(
      {
        repeat: current?.repeat ?? 'daily',
        interval: [
          current?.interval ?? DEFAULT_RECURRENCE_INTERVAL,
          [
            Validators.required,
            Validators.min(DEFAULT_RECURRENCE_INTERVAL),
            Validators.max(MAX_RECURRENCE_INTERVAL),
          ],
        ],
        skipWeekends: current?.skipWeekends ?? false,
        startsOn,
        endsOn: [
          current?.endsOn
            ? clampRecurrenceEndsOn(startsOn, current.endsOn)
            : defaultRecurrenceEndsOn(startsOn, current?.repeat ?? 'daily'),
          [endsOnValidator()],
        ],
      },
      undefined,
      (value) => this.draft.set(value),
    );
  }
}
